import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Fade,
  Chip,
  Alert,
  Divider,
  Autocomplete,
  useTheme,
  alpha
} from "@mui/material";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,

  Place as PlaceIcon
} from "@mui/icons-material";
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import styled from 'styled-components';
import { apiCall } from '../../../services/ApiCall';
import { useLocation, useNavigate } from 'react-router-dom';
import config from "../../../config/config.json";

// Styled Components
const StyledContainer = styled(Box)`
  background: linear-gradient(135deg, #f8faff 0%, #e3f2fd 100%);
  min-height: 100vh;
  padding: 24px;
`;
const HeaderWrapper = styled(Box)`
  margin-bottom: 24px;
  padding: 0 16px; 

`;

const HeaderCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(243, 243, 243, 1)  !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  }
`;

const FormCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(243, 243, 243, 1)  !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  margin-bottom: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(243, 243, 243, 1)  !important;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1) ;
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1) ;
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const StyledAutocomplete = styled(Autocomplete)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1) ;
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const ActionButton = styled(Button)`
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const PlacesAutocompleteWrapper = styled.div`
  position: relative;
  
  .places-input {
    width: 100%;
    padding: 16.5px 14px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1) ;
    }
    
    &:focus {
      outline: none;
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1) ;
      background: rgba(255, 255, 255, 1);
    }
  }
  
  .suggestions-container {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(243, 243, 243, 1) ;
    border: 1px solid rgba(13, 110, 253, 0.1);
    max-height: 200px;
    overflow-y: auto;
  }
  
  .suggestion-item {
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    
    &:hover, &.active {
      background-color: rgba(13, 110, 253, 0.08);
    }
    
    &:first-child {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    
    &:last-child {
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }
  }
`;

function SiteDetail({ onSwitchToContact, answer, setAnswer }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isDisabled, setIsDisabled] = useState(false);
  const [data, setData] = useState([]);
  const [saving, setSaving] = useState(false);
  const location = useLocation();
  const [companyData, setCompanyData] = useState([]);
  const [errors, setErrors] = useState({
    araId: '',
    companyId: null,
    state: '',
    street: ''
  });

  const getSitesList = async () => {
    const match = location.pathname.match(/\/site-list\/view\/(\d+)/);
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesList`,
      {},
      { companyId: match && match[1] },
      "GET"
    );
    if (isSuccess) {
      setData([...new Set(data?.data?.map(item => item.araId))]);
    }
  };

  const getCompanyList = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyList`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setCompanyData(data?.data?.reverse() || []);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getSitesList();
      getCompanyList();
    }
  }, []);

  // Validation functions
  const validateAraId = (value) => (!value || !value.trim() ? 'ARA ID is required' : '');
  const validateCompany = (value) => (!value ? 'Company is required' : '');
  const validateState = (value) => (!value || !value.trim() ? 'State is required' : '');
  const validateStreet = (value) => (!value || !value.trim() ? 'Street is required' : '');

  const changeAraId = (e) => {
    const value = e.target.value;
    let error = validateAraId(value);
    setAnswer(prevAnswer => ({ ...prevAnswer, araId: value }));

    if (data.includes(value.toString())) {
      error = 'This ARA ID already exists.';
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
    setErrors(prevErrors => ({ ...prevErrors, araId: error }));
  };

  const changeCompany = (event, selectedOption) => {
    const error = validateCompany(selectedOption);
    setAnswer((prevAnswer) => ({
      ...prevAnswer,
      companyId: selectedOption?.id ? Number(selectedOption.id) : '',
      company: selectedOption?.name || '',
    }));
    setErrors(prevErrors => ({ ...prevErrors, companyId: error }));
  };

  const changeAddress2 = (e) => {
    setAnswer(prevAnswer => ({ ...prevAnswer, address2: e.target.value }));
  };

  const changeState = (e) => {
    const value = e.target.value;
    const error = validateState(value);
    setAnswer(prevAnswer => ({ ...prevAnswer, state: value }));
    setErrors(prevErrors => ({ ...prevErrors, state: error }));
  };

  const changeCity = (e) => {
    setAnswer(prevAnswer => ({ ...prevAnswer, city: e.target.value }));
  };

  const changeZipcode = (e) => {
    setAnswer(prevAnswer => ({ ...prevAnswer, zipcode: e.target.value }));
  };

  const changeRecheckcycle = (e) => {
    setAnswer(prevAnswer => ({ ...prevAnswer, recheckcycle: e.target.value }));
  };

  const handleSelect = async (address) => {
    try {
      const results = await geocodeByAddress(address);
      const addressComponents = results[0].address_components;
      const getAddressComponent = (types) => {
        const component = addressComponents.find(comp => types.some(type => comp.types.includes(type)));
        return component ? component.long_name : '';
      };

      const street = getAddressComponent(['street_number']) + ' ' + getAddressComponent(['route']);
      const city = getAddressComponent(['locality']);
      const state = getAddressComponent(['administrative_area_level_1']);
      const zipcode = getAddressComponent(['postal_code']);

      setAnswer(prevAnswer => ({
        ...prevAnswer,
        address: street || address,
        city,
        state,
        zipcode,
        street: street || address
      }));
      setErrors(prevErrors => ({
        ...prevErrors,
        street: validateStreet(street || address),
        state: validateState(state)
      }));
    } catch (error) {
      console.error("Error fetching address details: ", error);
    }
  };

  const addNewSiteAndContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    const updatedAnswer = { ...answer, userData: [] };
    const { isSuccess } = await apiCall(
      `${config.POST_LOGIN_API_URL}addNewSitesAndContact`,
      {},
      updatedAnswer,
      "POST"
    );
    setSaving(false);
    if (isSuccess) {
      navigate("/site-list");
    }
  };

  const handleSubmit = (e) => {
    console.log("handleSubmit called");
    e.preventDefault();
   
    const araIdError = validateAraId(answer.araId);
    const companyError = validateCompany(answer?.companyId);
    const stateError = validateState(answer.state);
    const streetError = validateStreet(answer.street);


    if (araIdError || companyError || stateError || streetError) {
      setErrors({
        araId: araIdError,
        companyId: companyError,
        state: stateError,
        street: streetError
      });
      return;
    }

    onSwitchToContact();
  };

  const companyOptions = companyData.map((company) => ({
    id: company.id,
    name: company.name,
    label: company.name
  }));

  const selectedCompany = companyOptions.find(option => option.id === answer.companyId) || null;

  return (
    <StyledContainer>
      {/* Header Section */}
      <HeaderWrapper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            onClick={onSwitchToContact}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Add Site
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
              Step 1 of 2
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Site Details
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, height: '8px', background: alpha('#0d6efd', 0.2), borderRadius: '4px', overflow: 'hidden' }}>
            <Box sx={{ width: '50%', height: '100%', background: '#0d6efd', borderRadius: '4px' }} />
          </Box>
        </Box>
      </HeaderWrapper>

      <form onSubmit={handleSubmit}>
        {/* Site Information */}
        <Fade in timeout={500}>
          <FormCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BusinessIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                  Site Information
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: alpha('#0d6efd', 0.1) }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      ARA ID <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="Enter ARA ID"
                      value={answer.araId || ''}
                      onChange={changeAraId}
                      error={!!errors.araId}
                      helperText={errors.araId}
                      InputProps={{
                        startAdornment: (
                          <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />
                        ),
                      }}
                      required
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      Company <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <StyledAutocomplete
                      fullWidth
                      options={companyOptions}
                      getOptionLabel={(option) => option?.name || ''}

                      // ✅ Autocomplete expects the FULL OBJECT here
                      value={
                        companyOptions.find(
                          (company) => company.id === answer.companyId
                        ) || null
                      }

                      // ✅ Correct Autocomplete onChange
                      onChange={(event, newValue) => {
                        setAnswer(prev => ({
                          ...prev,
                         companyId: newValue ? Number(newValue.id) : null
                        }));
                      }}

                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Company"
                          error={!!errors.companyId}
                          helperText={errors.companyId}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          required
                        />
                      )}
                    />

                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </FormCard>
        </Fade>

        {/* Address Information */}
        <Fade in timeout={700}>
          <FormCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PlaceIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                  Address Information
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: alpha('#0d6efd', 0.1) }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 500,
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <PlaceIcon sx={{ mr: 1, fontSize: 20 }} />
                      Street Address *
                    </Typography>
                    <PlacesAutocompleteWrapper>
                      <PlacesAutocomplete
                        value={answer.street || ''}
                        onChange={(value) => setAnswer({ ...answer, street: value })}
                        onSelect={handleSelect}
                      >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                          <div>
                            <input
                              {...getInputProps({
                                placeholder: 'Start typing to search for address',
                                className: 'places-input'
                              })}
                            />
                            {(suggestions.length > 0 || loading) && (
                              <div className="suggestions-container">
                                {loading && (
                                  <div style={{ padding: '12px 16px', color: '#64748b' }}>
                                    Loading...
                                  </div>
                                )}
                                {suggestions.map((suggestion, index) => (
                                  <div
                                    key={index}
                                    {...getSuggestionItemProps(suggestion, {
                                      className: `suggestion-item ${suggestion.active ? 'active' : ''}`
                                    })}
                                  >
                                    {suggestion.description}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </PlacesAutocompleteWrapper>
                    {errors.street && (
                      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 2 }}>
                        {errors.street}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      Address 2
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="Apartment, suite, etc. (optional)"
                      value={answer.address2 || ''}
                      onChange={changeAddress2}
                      InputProps={{
                        startAdornment: (
                          <HomeIcon sx={{ color: '#64748b', mr: 1 }} />
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      City
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="Enter City"
                      value={answer.city || ''}
                      onChange={changeCity}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      State <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="Enter State"
                      value={answer.state || ''}
                      onChange={changeState}
                      error={!!errors.state}
                      helperText={errors.state}
                      required
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      ZIP Code
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="Enter ZIP Code"
                      value={answer.zipcode || ''}
                      onChange={changeZipcode}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </FormCard>
        </Fade>

        {/* Site Configuration */}
        <Fade in timeout={900}>
          <FormCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <RefreshIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                  Site Configuration
                </Typography>
                <Chip
                  label="Optional"
                  size="small"
                  sx={{
                    ml: 2,
                    background: alpha('#64748b', 0.1),
                    color: '#64748b',
                    fontWeight: 500
                  }}
                />
              </Box>

              <Divider sx={{ mb: 3, borderColor: alpha('#0d6efd', 0.1) }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                      Recheck Cycle
                    </Typography>
                    <StyledTextField
                      fullWidth
                      placeholder="e.g., 30 days"
                      value={answer.recheckcycle || ''}
                      onChange={changeRecheckcycle}
                      InputProps={{
                        startAdornment: (
                          <RefreshIcon sx={{ color: '#64748b', mr: 1 }} />
                        ),
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </FormCard>
        </Fade>

        {/* Error Alert */}
        {Object.values(errors).some(error => error) && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            Please correct the errors above before proceeding.
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <ActionButton
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={addNewSiteAndContact}
            disabled={isDisabled || saving}
            sx={{
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#0d6efd',
                color: '#0d6efd',
                background: alpha('#0d6efd', 0.05)
              }
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </ActionButton>

          <ActionButton
            type="submit"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            disabled={isDisabled || saving}
            sx={{
              background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a58ca 0%, #084298 100%)',
                boxShadow: '0 8px 25px rgba(13, 110, 253, 0.3)'
              }
            }}
          >
            Next
          </ActionButton>
        </Box>
      </form>
    </StyledContainer>
  );
}

export default SiteDetail;